import { ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { fetchy } from '@bridebook/web/lib/fetchy';
import { appError } from '@bridebook/web/lib/app/actions';
import { getCoupleChatUserOptions } from '@bridebook/web/lib/inbox/suppliers/selectors';
import { TalkJsManager } from '@bridebook/web/lib/inbox/suppliers/talkjs';
import { IEpic } from '@bridebook/web/lib/types';
import { noopAction } from '@bridebook/web/lib/utils';
import { MobileAppActionTypes, RegisterTalkJsDeviceAction } from '../action-types';

export const registerTalkJsDeviceEpic: IEpic<RegisterTalkJsDeviceAction> = (action$, { state$ }) =>
  action$.pipe(
    ofType(MobileAppActionTypes.REGISTER_TALKJS_DEVICE),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { token } = action.payload;
      const { platform } = state.mobileapp;
      const chatUserOptions = getCoupleChatUserOptions(state);

      if (!platform || !chatUserOptions) {
        return of(noopAction());
      }

      const getPromise = async () => {
        const talkManager = await TalkJsManager();
        const talkSession = await talkManager.get(chatUserOptions);
        if (!talkSession) throw new Error('Inbox session not initialized');

        /**
         * In order to always make sure the user is in TalkJS database, we need to
         * create it using TalkJS API. Creating it with SDK (this is what
         * essentially is done by TalkJSManager.get(chatUserOptions) method)
         * is not enough, because * it creates user only once Chatbox or Inbox
         * widget gets mounted.
         *
         * This is important specially for new users, because they won't have any
         * conversations, so the widget won't be mounted and user won't be created,
         * yet we will still call `setPushRegistration()` below.
         *
         * In case user already exists in TalkJS DB, this won't change anything
         * (for example user.updated event won't be triggered on TalkJS side).
         */
        await fetchy().authenticated.post(
          '/api/inbox/users/[id]/update',
          {
            id: chatUserOptions.id,
          },
          chatUserOptions,
        );

        /**
         * The `registerDevice()` is deprecated and, internally, will call `clearPushRegistrations()` before passing on to `setPushRegistration()`.
         * The `clearPushRegistrations()` method states the following in its documentation:
         * > Note that you must ensure that the user exists in the TalkJS database before you call this method.
         *
         * Failing to account for this will result in a request of the form:
         * > api/v0/-----//nyms/-----/push_registration
         *
         * Which will in turn result in the following error:
         * > [TalkJS], Cannot unregister this device. Please contact us to get more information.
         *
         * The consecutive forward slash in `//nyms/` represents an empty/missing internal User ID interpolation.
         * To maintain existing `registerDevice()` behaviour, we will call `setPushRegistration()` directly and conditionally invoke `clearPushRegistrations()`.
         */

        if (talkSession.me?.id) {
          try {
            await talkSession.clearPushRegistrations();
          } catch (error) {
            // Ignore
          }
        }

        await talkSession.setPushRegistration({
          provider: platform === 'ios' ? 'apns' : 'fcm',
          pushRegistrationId: token,
        });
      };

      return from(getPromise()).pipe(
        map(() => ({ type: MobileAppActionTypes.REGISTER_TALKJS_DEVICE_SUCCESS })),
        catchError((error) => of(appError({ error, feature: 'Register TalkJS Device' }))),
      );
    }),
  );
